import VideoPlayer from "@dana/Components/VideoPlayer";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    LinkFieldValue,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";


const enum fields {
    heading = "heading",
    image = "image",
    imageAltText = "imageAltText",
    video = "video",
    videoCaption = "videoCaption",
    watchVideoLabel = "watchVideoLabel",
    shortCoupleLabel = "shortCoupleLabel",
    shortCoupleUrl = "shortCoupleUrl",
    driveshaftLabel = "driveshaftLabel",
    driveshaftUrl = "driveshaftUrl",
    couplingShaftLabel = "couplingShaftLabel",
    couplingShaftUrl = "couplingShaftUrl",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.heading]: string;
        [fields.image]: string;
        [fields.imageAltText]: string;
        [fields.video]: LinkFieldValue;
        [fields.videoCaption]: LinkFieldValue;
        [fields.watchVideoLabel]: string;
        [fields.shortCoupleLabel]: string;
        [fields.shortCoupleUrl]: LinkFieldValue;
        [fields.driveshaftLabel]: string;
        [fields.driveshaftUrl]: LinkFieldValue;
        [fields.couplingShaftLabel]: string;
        [fields.couplingShaftUrl]: LinkFieldValue;
    };
}

export interface SelectReadyShaftTypeStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    image?: ImgProps;
    videoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const styles: SelectReadyShaftTypeStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 70%;
            gap: 24px;
            margin: 18px auto 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    heading: {
        variant: "h3",
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            border: 1px solid ${themeBackgrounds.lightGray};
            border-radius: 4px;
            padding: 16px;
            box-sizing: border-box;

            & > *:not(:last-child) {
                border-bottom: 1px solid ${themeBackgrounds.lightGray};
                padding-bottom: 16px;
                margin-bottom: 16px;
            }
        `,
    },
    image: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 480px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            gap: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                    `,
                        css`
                        flex-direction: column;
                        align-items: stretch;
                        gap: 14px;
                    `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
            variant: "primary",
            icon: {
                position: "left",
                src: () => <Youtube />
            },
        },
};

const EZIDSelectReadyShaftTypePage = ({ id, fields }: OwnProps) => {

    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = useGetLink(fields.video);
    const captionLink = useGetLink(fields.videoCaption);
    const shortCoupleLink = useGetLink(fields.shortCoupleUrl);
    const driveshaftLink = useGetLink(fields.driveshaftUrl);
    const couplingShaftLink = useGetLink(fields.couplingShaftUrl);

    const history = useHistory();
    const { isInShell } = useShellContext();

    const onButtonClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <Page>
            <Zone contentId={id} zoneName="Content" requireRows />
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h3">
                    {fields.heading}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    {fields.image && (
                        <Img src={fields.image} altText={fields.imageAltText || ''} {...styles.image} />
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Button variant="primary" onClick={() => onButtonClick(shortCoupleLink?.url)}>
                            {fields.shortCoupleLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onButtonClick(driveshaftLink?.url)}>
                            {fields.driveshaftLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onButtonClick(couplingShaftLink?.url)}>
                            {fields.couplingShaftLabel}
                        </Button>
                    </StyledWrapper>
                    {videoLink?.url && (
                        <Button {...styles.watchVideoButton} onClick={() => setVideoModalIsOpen(true)}>
                            {fields.watchVideoLabel}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="selectReadyShaftTypeVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </Page>
    )
};

const pageModule: PageModule = {
    component: EZIDSelectReadyShaftTypePage,
    definition: {
        displayName: "EZ-ID Select ReadyShaft Page",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Page Heading",
                defaultValue: "Select ReadyShaft Type",
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Page Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                displayName: "Page Image Alt Text",
                defaultValue: "",
            },
            {
                name: fields.video,
                fieldType: "Translatable",
                displayName: "Page Video",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                name: fields.videoCaption,
                fieldType: "Translatable",
                displayName: "Page Video Caption",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.watchVideoLabel,
                editorTemplate: "TextField",
                displayName: "Watch Video Label",
                defaultValue: "Watch The Video",
            },
            {
                fieldType: "Translatable",
                name: fields.shortCoupleLabel,
                editorTemplate: "TextField",
                displayName: "Short Couple Label",
                defaultValue: "1. Inter Axle Shaft / Short Couple",
            },
            {
                name: fields.shortCoupleUrl,
                fieldType: "Translatable",
                displayName: "Short Couple URL",
                tooltip: "URL to the 'Short Couple Selection' page",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.driveshaftLabel,
                editorTemplate: "TextField",
                displayName: "Driveshaft Label",
                defaultValue: "2. Driveshaft",
            },
            {
                name: fields.driveshaftUrl,
                fieldType: "Translatable",
                displayName: "Driveshaft URL",
                tooltip: "URL to the 'Driveshaft' page",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.couplingShaftLabel,
                editorTemplate: "TextField",
                displayName: "Coupling Shaft Label",
                defaultValue: "3. Coupling Shaft",
            },
            {
                name: fields.couplingShaftUrl,
                fieldType: "Translatable",
                displayName: "Coupling Shaft URL",
                tooltip: "URL to the 'Coupling Shaft' page",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
        ],
    },
};

export default pageModule;
